import {
	getCookie as browserStorageGetCookie,
	setCookie as browserStorageSetCookie,
	Status as CacheWriteStatus,
	deleteCookie,
} from '@atlassian/browser-storage-controls';

import type { CachedState, InvitedAppShortcutsCache } from '../types';

import { getConfig } from './config';

const cacheKey = 'invitedAppShortcuts';
const localStorageCacheDebugKey = `${cacheKey}Debug`;

export { CacheWriteStatus };

type GetCookie = (key: string, ssr?: boolean) => string | undefined;

type SetCookie = (
	...[key, value, attributes]: Parameters<typeof browserStorageSetCookie>
) => Promise<SetCookieStringStatus | boolean | undefined>;

// Browser storage setCookie has a weak return type (string),
// but we're listing the statuses here that it actually returns:
type SetCookieStringStatus = 'SUCCESS' | 'BLOCKED' | 'FAILED' | string;

export class InviteAppShortcutsCookieCache implements InvitedAppShortcutsCache {
	private getCookie: GetCookie;
	private setCookie: SetCookie;
	private lastParsedRawValue: string | null = null;
	private lastParsedState: CachedState | null = null;

	constructor({
		getCookie = browserStorageGetCookie,
		setCookie = browserStorageSetCookie,
	}: {
		getCookie?: GetCookie;
		setCookie?: SetCookie;
	} = {}) {
		this.getCookie = getCookie;
		this.setCookie = setCookie;
	}

	async write(state: CachedState): Promise<boolean> {
		if (cacheDebugMode() === 'local-storage') {
			localStorage[cacheKey] = JSON.stringify(state);
			return true;
		}
		const result = await this.setCookie(cacheKey, JSON.stringify(state), {
			expires: new Date(Date.now() + getConfig().CACHE_TIME_UNTIL_EXPIRY),
			sameSite: 'strict',
		});
		return result === true || result === CacheWriteStatus.SUCCESS;
	}

	read({ ssr }: { ssr: boolean }): CachedState | null {
		const throwOnError = ssr;
		if (cacheDebugMode() === 'local-storage') {
			if (localStorage?.[cacheKey]) {
				return this.parseCachedState({ rawValue: localStorage[cacheKey], throwOnError });
			}
		} else {
			const cookieValueString = this.getCookie(cacheKey, ssr);
			if (cookieValueString) {
				return this.parseCachedState({ rawValue: cookieValueString, throwOnError });
			}
		}
		return null;
	}

	private parseCachedState({
		rawValue,
		throwOnError,
	}: {
		rawValue: string;
		throwOnError: boolean;
	}): CachedState | null {
		// Reuse parsed state for identical cookie/localStorage payloads during the same render lifecycle.
		if (this.lastParsedRawValue === rawValue) {
			return this.lastParsedState;
		}
		try {
			const decoded = isUrlEncodedJson(rawValue) ? decodeURIComponent(rawValue) : rawValue;
			const parsed = (JSON.parse(decoded) as CachedState) || null;
			this.lastParsedRawValue = rawValue;
			this.lastParsedState = parsed;
			return parsed;
		} catch (error) {
			if (throwOnError) {
				throw error;
			}
			// Subsequent client reads: swallow parse errors for robustness; treat as cache miss.
			this.lastParsedRawValue = rawValue;
			this.lastParsedState = null;
			return null;
		}
	}
}

const isUrlEncodedJson = (json?: string) =>
	json &&
	(json.startsWith(`{%22`) || // `{"` with only `"` encoded -- this was value was observed in Confluence
		json.startsWith(`%7B%22`)); // `{"` encoded

const cacheDebugMode = () => {
	const debugMode =
		typeof localStorage !== 'undefined' &&
		localStorage &&
		// Extra safety while we're in the debug-only phase,
		// before we've switched to the proper browser controls (to be removed when that happens).
		// Requires manually setting localStorage.invitedAppShortcutsDebug = "true" in the console
		// to allow our code to write anything into the local storage cache.
		localStorage[localStorageCacheDebugKey];
	if (debugMode === 'cookie-delete') {
		localStorage[localStorageCacheDebugKey] = null;
		deleteCookie(cacheKey);
	} else if (debugMode && !['null', 'false'].includes(debugMode)) {
		return 'local-storage';
	}
	return null;
};
